import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import {
  useUpdateContract,
  useDeleteContract,
  getContractDetailsQuery,
} from '@/app/hooks/queries/useContracts';
import {
  Field,
  FieldControl,
  FieldDescription,
  FieldError,
  FieldLabel,
} from '@/app/ui/components/form/field';
import { Button } from '@/app/ui/components/button';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogPopup,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/app/ui/components/alert-dialog';
import { useTranslations } from 'use-intl';
import { use, useEffect, useState } from 'react';
import { AppContext } from '@/app/appContext';
import { useGetCompaniesQuery } from '@/app/hooks/queries/useCompanies';
import { Company } from '@/app/types/models';
import { CompanyCombobox } from '@/app/components/features/companies/CompanyCombobox';
import { Form } from '@/app/ui/components/form';
import {
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectTriggerIcon,
  SelectValue,
} from '@/app/ui/components/select';
import { EvaluationCreatorTypeLabelsEnum, LangEnum } from '@/app/utils/constants';
import { getLangIcon } from '@/app/utils/language';
import { Card } from '@/app/ui/components/card';
import { H3 } from '@/app/ui/components/heading';
import { useSuspenseQuery } from '@tanstack/react-query';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/contracts/$contractId/_layout/(home)/settings/_layout/',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.tenderPackageSettings');
  const tGlobal = useTranslations();
  const { contractId, projectId } = useParams({
    from: '/_authed/project/$projectId/_layout/(evaluation)/contracts/$contractId/_layout/(home)/settings/_layout',
  });
  const { userMeta } = use(AppContext);
  const navigate = useNavigate();
  const { data: contract } = useSuspenseQuery(getContractDetailsQuery(contractId));
  const { mutateAsync: updateContract, isPending: isUpdating } = useUpdateContract();
  const { mutateAsync: deleteContract, isPending: isDeleting } = useDeleteContract();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [name, setName] = useState('');
  const [language, setLanguage] = useState<string>(LangEnum.en);
  const [creatorType, setCreatorType] = useState<EvaluationCreatorTypeLabelsEnum>(
    EvaluationCreatorTypeLabelsEnum.CLIENT,
  );
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

  const organizationId = userMeta?.organizationId ?? '';
  const { data: companies = [] } = useGetCompaniesQuery({
    organizationId,
    searchText: '',
    enabled: !!organizationId,
  });

  const creatorTypes = [
    { value: EvaluationCreatorTypeLabelsEnum.CLIENT, label: t('formLabels.client') },
    { value: EvaluationCreatorTypeLabelsEnum.SUPPLIER, label: t('formLabels.supplier') },
  ];

  // Initialize form values when contract data loads
  useEffect(() => {
    if (name === '') {
      setName(contract.name ?? '');
      setLanguage(contract.language ?? LangEnum.en);
    }

    setCreatorType(EvaluationCreatorTypeLabelsEnum.SUPPLIER);

    if (companies.length > 0 && contract.companyId) {
      const company = companies.find((c) => c.id === contract.companyId);
      setSelectedCompany(company ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract, userMeta, companies]);

  const handleSubmit = async () => {
    await updateContract({
      name,
      language,
      companyId: selectedCompany?.id,
      projectId: contract?.projectId,
    });
  };

  const handleDelete = async () => {
    await deleteContract(contractId);
    navigate({ to: '/project/$projectId', params: { projectId } });
    setDeleteDialogOpen(false);
  };

  return (
    <>
      <Form onFormSubmit={handleSubmit} className="max-w-xl w-full flex flex-col gap-4">
        <Card className="space-y-4">
          <H3 className="-mt-1">{t('info')}</H3>
          <Field>
            <FieldLabel>{t('formLabels.name')}</FieldLabel>
            <FieldControl
              required
              value={name}
              onValueChange={setName}
              placeholder={t('formLabels.name')}
              maxLength={100}
            />
            <FieldDescription>{t('formLabels.nameDescription')}</FieldDescription>
            <FieldError />
          </Field>
          <Field>
            <FieldLabel>{t('formLabels.role')}</FieldLabel>
            <Select
              required
              value={creatorType}
              onValueChange={(value) => setCreatorType(value as EvaluationCreatorTypeLabelsEnum)}
            >
              <SelectTrigger size="lg" className={'w-full'}>
                <SelectValue>
                  {(v) => creatorTypes.find((item) => item.value === v)?.label}
                </SelectValue>
                <SelectTriggerIcon />
              </SelectTrigger>
              <SelectPopup>
                {creatorTypes.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectPopup>
            </Select>
            <FieldDescription>{t('formLabels.roleDescription')}</FieldDescription>
            <FieldError />
          </Field>
        </Card>
        <Card className="space-y-4">
          <H3 className="-mt-1">{t('projectClient')}</H3>
          <Field>
            <FieldLabel>{t('formLabels.client')}</FieldLabel>
            <CompanyCombobox
              companies={companies}
              value={selectedCompany}
              onValueChange={setSelectedCompany}
              disabled={isUpdating}
              ariaLabel={t('formLabels.client')}
            />
            <FieldError />
          </Field>
        </Card>
        <Card className="space-y-4">
          <H3 className="-mt-1">{t('analysisSettings')}</H3>
          <Field>
            <FieldLabel>{t('formLabels.language')}</FieldLabel>
            <Select required value={language} onValueChange={(lang) => setLanguage(lang as never)}>
              <SelectTrigger size="lg" className={'w-full'}>
                <SelectValue>
                  {(v) => (
                    <>
                      {getLangIcon(v as LangEnum)}
                      {tGlobal(
                        // @ts-expect-error ...
                        `general.languages.${v}`,
                      )}
                    </>
                  )}
                </SelectValue>
                <SelectTriggerIcon />
              </SelectTrigger>
              <SelectPopup>
                {Object.values(LangEnum).map((lang) => (
                  <SelectItem key={lang} value={lang}>
                    {getLangIcon(lang)}
                    {tGlobal(`general.languages.${lang}`)}
                  </SelectItem>
                ))}
              </SelectPopup>
            </Select>
            <FieldError />
          </Field>
        </Card>
        <Button type="submit" pending={isUpdating} disabled={isUpdating} className="ml-auto">
          {t('buttons.save')}
        </Button>
        <Card>
          <H3 className="-mt-1">{t('danger')}</H3>
          <p className="text-sm text-muted mt-2 mb-4">{t('dangerText')}</p>
          <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <AlertDialogTrigger
              render={<Button variant="destructive">{t('deleteConfirmModal.title')}</Button>}
            />
            <AlertDialogPopup>
              <AlertDialogTitle>{t('deleteConfirmModal.title')}</AlertDialogTitle>
              <AlertDialogDescription>
                {tGlobal.rich('pages.contractSettings.deleteConfirmModal.subtitle', {
                  contractName: contract.name,
                  b: (chunks) => <strong>{chunks}</strong>,
                })}
              </AlertDialogDescription>
              <AlertDialogFooter>
                <AlertDialogCancel />
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  pending={isDeleting}
                  disabled={isDeleting}
                >
                  {t('buttons.delete')}
                </Button>
              </AlertDialogFooter>
            </AlertDialogPopup>
          </AlertDialog>
        </Card>
      </Form>
    </>
  );
}
